import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import useWs from '../lib/useWs';
import { fmtAED } from '../lib/format';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { Textarea } from '../components/ui/textarea';
import { Search, Plus, Minus, Trash2, ShoppingCart, Printer, CheckCircle2, X, UserPlus, ChefHat, Grid3x3, StickyNote, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import InvoiceView from '../components/InvoiceView';

const KOT_PRESETS = ['No onion', 'No garlic', 'Extra spicy', 'Less spicy', 'No salt', 'Less oil', 'Well done', 'Half done', 'No sauce', 'Extra sauce', 'No coriander', 'Jain', 'No chilli', 'Cut in half', 'Serve later', 'On the side'];
const AGGREGATOR_CHANNELS = [['talabat', 'Talabat'], ['deliveroo', 'Deliveroo'], ['careem', 'Careem'], ['noonfood', 'Noon Food'], ['keeta', 'Keeta'], ['smiles', 'Smiles']];
const PL_KEY = 'robochef_pos_pricelist';

// Single source of truth for line pricing: pricelist override → channel price → dining price
const resolvePrice = (item, { menuId, orderType, channel }) => {
  const pl = (item.pricelist_prices || {})[menuId];
  if (pl != null) return pl;
  if (orderType === 'delivery' && item.price_delivery != null) return item.price_delivery;
  if (orderType === 'agent') {
    const appPrice = item[`price_${channel}`];
    if (appPrice != null) return appPrice;
    if (item.price_agent != null) return item.price_agent;
    if (item.price_delivery != null) return item.price_delivery;
  }
  return item.price;
};
const resolveVariantPrice = (variant, { orderType }) => {
  if (orderType === 'delivery' && variant.price_delivery != null) return variant.price_delivery;
  if (orderType === 'agent' && variant.price_agent != null) return variant.price_agent;
  return variant.price;
};

export default function POS() {
  const { tenant, currentOutlet } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [allItems, setAllItems] = useState([]);
  const [menusList, setMenusList] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [cats, setCats] = useState([]);
  const [tables, setTables] = useState([]);
  const [activeCat, setActiveCat] = useState('all');
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [orderType, setOrderType] = useState('dine-in');
  const [selectedTable, setSelectedTable] = useState(null);
  const [existingOrderId, setExistingOrderId] = useState(null);
  const [autoPay, setAutoPay] = useState(false);
  const [pendingTableId, setPendingTableId] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [kotSent, setKotSent] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('flat');
  const [orderNotes, setOrderNotes] = useState('');
  const [orderChannel, setOrderChannel] = useState('talabat');
  const [extOrderNo, setExtOrderNo] = useState('');
  const [customer, setCustomer] = useState(null);
  const [custQuery, setCustQuery] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const [tableChecks, setTableChecks] = useState([]);
  const [checkPickOpen, setCheckPickOpen] = useState(false);
  const [conflict, setConflict] = useState(null); // { table, checks } — occupied table picked with unsaved cart
  const [payOpen, setPayOpen] = useState(false);
  const [payments, setPayments] = useState([{ method: 'cash', amount: 0 }]);
  const [invoice, setInvoice] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [cyoItem, setCyoItem] = useState(null);
  const [noteTarget, setNoteTarget] = useState(null); // 'order' or a cart item key
  const [noteText, setNoteText] = useState('');
  const [cyoSel, setCyoSel] = useState({});
  const [config, setConfig] = useState(null);
  const [tip, setTip] = useState(0);

  const tableLock = useRef(false);
  const hydrated = useRef(false);
  const cartRef = useRef(cart); cartRef.current = cart;
  const itemsRef = useRef(allItems); itemsRef.current = allItems;
  const menuRef = useRef(selectedMenuId); menuRef.current = selectedMenuId;
  const otypeRef = useRef(orderType); otypeRef.current = orderType;
  const chanRef = useRef(orderChannel); chanRef.current = orderChannel;
  const DRAFT_KEY = `robochef_pos_draft_${currentOutlet?.id || 'default'}`;

  const loadTables = useCallback(async () => {
    if (!currentOutlet) return;
    const { data } = await api.get('/tables', { params: { outlet_id: currentOutlet.id } });
    setTables(data);
    return data;
  }, [currentOutlet]);

  const reloadMenu = useCallback(async () => {
    const [mi, mn] = await Promise.all([api.get('/menu-items'), api.get('/menus').catch(() => ({ data: [] }))]);
    const menus = mn.data || [];
    setMenusList(menus);
    setAllItems(mi.data.filter((i) => i.available));
    setSelectedMenuId((cur) => {
      if (cur && menus.some((m) => m.id === cur)) return cur;
      const saved = localStorage.getItem(PL_KEY);
      if (saved && menus.some((m) => m.id === saved)) return saved;
      return menus.find((m) => m.is_active)?.id || null;
    });
  }, []);

  useEffect(() => {
    (async () => {
      const c = await api.get('/categories');
      setCats(c.data);
      await reloadMenu();
    })();
    api.get('/config').then((r) => setConfig(r.data)).catch(() => {});
    // restore an interrupted draft (navigation away/back, refresh, crash)
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.cart?.length || d.existingOrderId) {
          setCart(d.cart || []);
          setExistingOrderId(d.existingOrderId || null);
          setOrderType(d.orderType || 'dine-in');
          setOrderChannel(d.orderChannel || 'talabat');
          setExtOrderNo(d.extOrderNo || '');
          setOrderNotes(d.orderNotes || '');
          setDiscount(d.discount || 0);
          setDiscountType(d.discountType || 'flat');
          setDeliveryAddress(d.deliveryAddress || '');
          setKotSent(!!d.kotSent);
          setCustomer(d.customer || null);
          if (d.tableId) setPendingTableId(d.tableId);
          toast.info('Unsaved order restored');
        }
      }
    } catch (e) {}
    hydrated.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // persist the working draft so refresh/navigation never loses unsaved work
  useEffect(() => {
    if (!hydrated.current) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        cart, tableId: selectedTable?.id || null, existingOrderId, orderType, orderChannel,
        extOrderNo, orderNotes, discount, discountType, deliveryAddress, kotSent, customer,
      }));
    } catch (e) {}
  }, [cart, selectedTable, existingOrderId, orderType, orderChannel, extOrderNo, orderNotes, discount, discountType, deliveryAddress, kotSent, customer, DRAFT_KEY]);
  const clearDraft = useCallback(() => { try { localStorage.removeItem(DRAFT_KEY); } catch (e) {} }, [DRAFT_KEY]);

  // live sync: menu edits elsewhere -> refresh catalog + offer controlled cart recalculation;
  // table changes elsewhere -> refresh floor state. Cart contents are never auto-touched.
  useWs(tenant?.id, useCallback((msg) => {
    if (msg.type === 'menu_updated') {
      reloadMenu().then(() => {
        if (cartRef.current.length) {
          toast('Menu or prices were updated', {
            description: 'Your cart kept its current prices.',
            duration: 12000,
            action: { label: 'Recalculate cart', onClick: () => repriceCart() },
          });
        }
      });
    }
    if (msg.type === 'table_update') loadTables();
    if (msg.type === 'aggregator_inbox_new') {
      // Incoming delivery order: open the bill automatically when idle; while the
      // cashier is busy, preserve the cart and show a prominent one-click alert.
      const ch = (msg.channel || 'aggregator').toUpperCase();
      if (cartRef.current.length) {
        toast(`New ${ch} delivery order`, {
          description: 'Your current cart is untouched — review the delivery order when ready.',
          duration: 15000,
          action: { label: 'Open incoming orders', onClick: () => nav('/aggregator') },
        });
      } else {
        toast.info(`New ${ch} delivery order — opening incoming orders`);
        nav('/aggregator');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadMenu, loadTables]));

  // Pricelist (menu set) behaviour on POS:
  //  • catalog = the ★ active menu's items
  //  • a pricelist's OWN items replace same-named main items (per-pricelist variants/prices) and
  //    pricelist-only items are added
  //  • main-catalog items pick up pricelist_prices overrides via priceFor()
  //  • never blank: falls back to all available items
  const activeMenuId = menusList.find((m) => m.is_active)?.id || null;
  const items = useMemo(() => {
    const activeList = (activeMenuId ? allItems.filter((i) => i.menu_id === activeMenuId) : []);
    const base = activeList.length ? activeList : allItems;
    const own = (selectedMenuId && selectedMenuId !== activeMenuId)
      ? allItems.filter((i) => i.menu_id === selectedMenuId) : [];
    if (!own.length) return base;
    const ownNames = new Set(own.map((i) => i.name.trim().toLowerCase()));
    return [...base.filter((i) => !ownNames.has(i.name.trim().toLowerCase())), ...own];
  }, [allItems, selectedMenuId, activeMenuId]);
  useEffect(() => { loadTables(); }, [loadTables]);

  // deep-link from Floor Plan: /pos?table=<id>  (&new=1 forces a fresh check)
  useEffect(() => {
    const tid = params.get('table');
    if (tid && tables.length) {
      const t = tables.find((x) => x.id === tid);
      if (t) { pickTable(t, params.get('new') === '1'); setParams({}, { replace: true }); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables]);

  // deep-link from Orders "Edit" / table check: /pos?order=<id> (&pay=1 opens settlement)
  useEffect(() => {
    const oid = params.get('order');
    if (!oid) return;
    const wantPay = params.get('pay') === '1';
    (async () => {
      try {
        const { data } = await api.get(`/orders/${oid}`);
        if (data.status === 'completed' || data.status === 'cancelled') {
          toast.error('This order is already closed and cannot be edited.');
          setParams({}, { replace: true });
          return;
        }
        setExistingOrderId(data.id);
        setOrderType(data.type || 'dine-in');
        setCart(orderItemsToCart(data.items || []));
        setOrderNotes(data.notes || '');
        setDeliveryAddress(data.delivery_address || '');
        setKotSent(!!data.kot_printed);
        if (data.discount_type === 'percent') setDiscountType('percent');
        if (data.table_id) setPendingTableId(data.table_id);
        if (wantPay) setAutoPay(true);
        else toast.info(`Editing order ${data.order_no}`);
      } catch (e) { toast.error('Could not load order'); }
      finally { setParams({}, { replace: true }); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables]);

  // resolve an order's table once the tables list is available
  useEffect(() => {
    if (pendingTableId && tables.length) {
      const t = tables.find((x) => x.id === pendingTableId);
      if (t) setSelectedTable(t);
      setPendingTableId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTableId, tables]);

  // once a settle-linked order's cart is loaded (and its table resolved), open the payment sheet
  useEffect(() => {
    if (autoPay && cart.length && !pendingTableId) { setAutoPay(false); startCheckout(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPay, cart, pendingTableId]);

  const orderItemsToCart = (orderItems) => orderItems.map((it, i) => ({
    key: it.menu_item_id + '_' + i, menu_item_id: it.menu_item_id, name: it.name,
    qty: it.qty, unit_price: it.unit_price, station: it.station || 'Hot Kitchen',
    modifiers: it.modifiers || [], tax_exempt: it.tax_exempt || false, notes: it.notes || '',
    variant: it.variant || null,
  }));

  const loadCheck = (data) => {
    setExistingOrderId(data.id);
    setOrderType(data.type || 'dine-in');
    setCart(orderItemsToCart(data.items || []));
    setOrderNotes(data.notes || '');
    setDeliveryAddress(data.delivery_address || '');
    setKotSent(!!data.kot_printed);
    if (data.discount_type === 'percent') setDiscountType('percent');
    if (data.table_id && tables.length) {
      const t = tables.find((x) => x.id === data.table_id);
      if (t) setSelectedTable(t);
    }
    toast.info(`Loaded ${data.order_no}`);
  };

  const freshCheckState = () => { setExistingOrderId(null); setCart([]); setOrderNotes(''); setKotSent(false); setDiscount(0); setDiscountType('flat'); };

  const pickTable = async (t, forceNew = false) => {
    if (tableLock.current) return; // guard against rapid double-taps
    tableLock.current = true;
    try {
      if (t.id === selectedTable?.id) { setTableOpen(false); return; }
      const hasWork = cartRef.current.length > 0;
      let checks = [];
      try { const { data } = await api.get(`/tables/${t.id}/checks`); checks = data; } catch (e) {}
      if (hasWork && !forceNew) {
        if (!checks.length) {
          // free table: attach the table to the current cart, never drop the items
          setOrderType('dine-in'); setSelectedTable(t); setTableOpen(false);
          toast.info(`Cart assigned to ${t.name}`);
          return;
        }
        // occupied table: explicit conflict choice — never silently overwrite or merge
        setConflict({ table: t, checks }); setTableOpen(false);
        return;
      }
      setOrderType('dine-in');
      setSelectedTable(t);
      setTableOpen(false);
      if (!forceNew) {
        if (checks.length === 1) { loadCheck(checks[0]); return; }
        if (checks.length > 1) {
          freshCheckState();
          setTableChecks(checks); setCheckPickOpen(true);
          toast.info(`${t.name} has ${checks.length} checks — pick one`);
          return;
        }
        if (!checks.length && t.current_order_id) {
          try {
            const { data } = await api.get(`/orders/${t.current_order_id}`);
            if (data.status !== 'completed' && data.status !== 'cancelled') { loadCheck(data); return; }
          } catch (e2) {}
        }
      }
      freshCheckState();
      if (forceNew) toast.info(`New check on ${t.name}`);
    } finally { tableLock.current = false; }
  };

  // detach the table but keep the cart — items are never silently dropped
  const detachTable = () => {
    setSelectedTable(null);
    if (existingOrderId) toast.info('Table detached — cart kept. Pick a table before sending to kitchen.');
  };

  const conflictKeepCart = () => {
    setSelectedTable(conflict.table); setOrderType('dine-in');
    setExistingOrderId(null); setKotSent(false);
    toast.info(`Cart kept — new check on ${conflict.table.name}`);
    setConflict(null);
  };
  const conflictTransfer = async () => {
    try {
      await api.post(`/orders/${existingOrderId}/transfer`, { table_id: conflict.table.id });
      setSelectedTable(conflict.table);
      toast.success(`Order moved to ${conflict.table.name}`);
      loadTables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
    setConflict(null);
  };
  const conflictOpenCheck = () => {
    const { table, checks } = conflict;
    setCart([]); setDiscount(0); setDiscountType('flat'); setCustomer(null); setDeliveryAddress(''); setExtOrderNo('');
    setSelectedTable(table); setOrderType('dine-in');
    if (checks.length === 1) loadCheck(checks[0]);
    else { freshCheckState(); setSelectedTable(table); setTableChecks(checks); setCheckPickOpen(true); }
    setConflict(null);
  };

  const newOrder = () => {
    if (cart.length && !window.confirm('Discard the current order and start a new one?')) return;
    setCart([]); setDiscount(0); setCustomer(null); setSelectedTable(null); setExistingOrderId(null);
    setOrderNotes(''); setTip(0); setDeliveryAddress(''); setKotSent(false); setDiscountType('flat'); setExtOrderNo('');
    clearDraft();
    toast.info('New order started');
  };

  const setLineNote = (key, note) => setCart((prev) => prev.map((c) => c.key === key ? { ...c, notes: note } : c));
  const openKotNoteOrder = () => { setNoteText(orderNotes || ''); setNoteTarget('order'); };
  const openKotNote = (item) => { setNoteText(item.notes || ''); setNoteTarget(item.key); };
  const addPreset = (p) => setNoteText((t) => (t ? t + ', ' + p : p));
  const attachKotNote = () => {
    if (noteTarget === 'order') setOrderNotes(noteText.trim());
    else if (noteTarget) setLineNote(noteTarget, noteText.trim());
    setNoteTarget(null);
  };

  // search ignores the category filter and matches name AND item code (SKU), case-insensitive
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q) return items.filter((i) => i.name.toLowerCase().includes(q) || (i.code || '').toLowerCase().includes(q));
    if (activeCat !== 'all') return items.filter((i) => i.category_id === activeCat);
    return items;
  }, [items, activeCat, search]);

  const priceFor = useCallback((item) => resolvePrice(item, { menuId: selectedMenuId, orderType, channel: orderChannel }), [orderType, orderChannel, selectedMenuId]);
  const variantPriceFor = (variant) => resolveVariantPrice(variant, { orderType });

  // explicit pricelist change: reprice unpaid cart lines against the new list.
  // Lines whose item no longer exists keep their last known price (visible fallback, never zero).
  // Reads via refs so WS-toast actions and stale closures always use current state.
  const repriceCart = useCallback((menuId) => {
    const mid = menuId || menuRef.current;
    const src = itemsRef.current;
    const otype = otypeRef.current, chan = chanRef.current;
    let kept = 0;
    const next = cartRef.current.map((line) => {
      const item = src.find((i) => i.id === line.menu_item_id);
      let base = null;
      if (item) {
        if (line.variant) {
          const v = (item.variants || []).find((x) => x.name === line.variant);
          if (v) base = resolveVariantPrice(v, { orderType: otype });
        } else {
          base = resolvePrice(item, { menuId: mid, orderType: otype, channel: chan });
        }
      }
      if (base == null) { kept += 1; return line; }
      const modDelta = (line.modifiers || []).reduce((s, m) => s + (m.price_delta || 0), 0);
      return { ...line, unit_price: Number((base + modDelta).toFixed(2)) };
    });
    setCart(next);
    return kept;
  }, []);

  const changePricelist = (menuId) => {
    const name = menusList.find((m) => m.id === menuId)?.name;
    setSelectedMenuId(menuId);
    localStorage.setItem(PL_KEY, menuId);
    setActiveCat('all');
    if (cartRef.current.length) {
      const kept = repriceCart(menuId);
      toast.success(`Pricelist: ${name} — cart repriced${kept ? ` (${kept} line(s) kept their price)` : ''}`);
    } else {
      toast.info(`Pricelist: ${name}`);
    }
  };

  const addToCart = (item, variant) => {
    const price = variant ? variantPriceFor(variant) : priceFor(item);
    const key = item.id + (variant ? '_' + variant.name : '');
    setCart((prev) => {
      const ex = prev.find((c) => c.key === key);
      if (ex) return prev.map((c) => c.key === key ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { key, menu_item_id: item.id, name: item.name + (variant ? ` (${variant.name})` : ''), variant: variant?.name || null, qty: 1, unit_price: price, station: item.station, modifiers: [], tax_exempt: item.tax_exempt }];
    });
  };
  const changeQty = (key, delta) => setCart((prev) => prev.map((c) => c.key === key ? { ...c, qty: Math.max(1, c.qty + delta) } : c).filter((c) => c.qty > 0));
  const removeItem = (key) => setCart((prev) => prev.filter((c) => c.key !== key));

  const openCYO = (item) => { setCyoItem(item); setCyoSel({}); };
  const toggleOpt = (gi, oi, max) => setCyoSel((prev) => {
    const cur = prev[gi] || [];
    if (cur.includes(oi)) return { ...prev, [gi]: cur.filter((x) => x !== oi) };
    if (max === 1) return { ...prev, [gi]: [oi] };
    if (cur.length < max) return { ...prev, [gi]: [...cur, oi] };
    return prev;
  });
  const cyoValid = cyoItem ? (cyoItem.option_groups || []).every((g, gi) => (cyoSel[gi] || []).length >= (g.min || 1)) : false;
  const addConfigured = () => {
    const mods = []; let delta = 0;
    (cyoItem.option_groups || []).forEach((g, gi) => {
      (cyoSel[gi] || []).forEach((oi) => {
        const opt = g.options[oi];
        mods.push({ group: g.name, name: opt.name, price_delta: opt.price_delta || 0, recipe: opt.recipe || [] });
        delta += opt.price_delta || 0;
      });
    });
    const key = cyoItem.id + '_' + Date.now();
    setCart((prev) => [...prev, { key, menu_item_id: cyoItem.id, name: cyoItem.name, variant: null, qty: 1, unit_price: cyoItem.price + delta, station: cyoItem.station, modifiers: mods, tax_exempt: cyoItem.tax_exempt }]);
    setCyoItem(null); setCyoSel({});
  };

  const subtotal = cart.reduce((s, c) => s + c.unit_price * c.qty, 0);
  const discVal = discountType === 'percent' ? subtotal * discount / 100 : Number(discount || 0);
  const taxable = Math.max(0, subtotal - discVal);
  const vat = cart.reduce((s, c) => s + (c.tax_exempt ? 0 : c.unit_price * c.qty), 0) * (subtotal ? (taxable / subtotal) : 0) * 0.05;
  const svcRate = config?.business?.service_charge_rate || 0;
  const serviceCharge = taxable * svcRate;
  const total = taxable + vat + serviceCharge;
  const payMethods = config?.business?.payment_methods?.length ? config.business.payment_methods : ['cash', 'card', 'upi', 'wallet'];
  const tipEnabled = config?.business?.tip_enabled;
  const enabledOrderTypes = config?.business?.enabled_order_types?.length ? config.business.enabled_order_types : ['dine-in', 'takeaway', 'delivery', 'qr'];

  const searchCust = async (q) => {
    setCustQuery(q);
    if (q.length < 2) { setCustResults([]); return; }
    const { data } = await api.get('/customers', { params: { q } });
    setCustResults(data);
  };

  const buildItems = () => cart.map((c) => ({ menu_item_id: c.menu_item_id, name: c.name, qty: c.qty, unit_price: c.unit_price, station: c.station, modifiers: c.modifiers, tax_exempt: c.tax_exempt, notes: c.notes || '', variant: c.variant || null }));

  // create order if none, else update items on existing
  const ensureOrder = async () => {
    if (existingOrderId) {
      await api.put(`/orders/${existingOrderId}/items`, {
        outlet_id: currentOutlet.id, type: orderType, items: buildItems(),
        notes: orderNotes, discount: Number(discount || 0), discount_type: discountType,
        service_charge_rate: svcRate, delivery_address: deliveryAddress,
      });
      return existingOrderId;
    }
    const { data } = await api.post('/orders', {
      outlet_id: currentOutlet.id, type: orderType, channel: orderType === 'agent' ? orderChannel : 'pos',
      external_order_no: orderType === 'agent' ? (extOrderNo.trim() || null) : null,
      table_id: orderType === 'dine-in' ? (selectedTable?.id || null) : null,
      customer_id: customer?.id || null, notes: orderNotes,
      delivery_address: deliveryAddress, customer_phone: customer?.phone || '',
      discount: Number(discount || 0), discount_type: discountType,
      service_charge_rate: svcRate,
      items: buildItems(),
    });
    setExistingOrderId(data.id);
    return data.id;
  };

  const sendToKitchen = async () => {
    if (!cart.length) { toast.error('Add items first'); return; }
    if (orderType === 'dine-in' && !selectedTable) { toast.error('Select a table for dine-in'); return; }
    setProcessing(true);
    try {
      const oid = await ensureOrder();
      const { data } = await api.post(`/orders/${oid}/confirm`);
      setKotSent(true);
      toast.success(`Sent to kitchen · ${data.kot_no}`);
      if (data.low_stock_alerts?.length) toast.warning(`Low stock: ${data.low_stock_alerts.map((a) => a.ingredient).join(', ')}`);
      loadTables();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  const saveOrder = async () => {
    if (!cart.length) { toast.error('Add items first'); return; }
    if (orderType === 'dine-in' && !selectedTable) { toast.error('Select a table for dine-in'); return; }
    setProcessing(true);
    try { await ensureOrder(); toast.success('Order saved (no KOT sent)'); loadTables(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setProcessing(false); }
  };
  const printBill = () => { window.print(); };

  const startCheckout = () => {
    if (!cart.length) { toast.error('Cart is empty'); return; }
    if (orderType === 'dine-in' && !selectedTable) { toast.error('Select a table for dine-in'); return; }
    setPayments([{ method: 'cash', amount: Number(total.toFixed(2)) }]);
    setPayOpen(true);
  };

  const finalize = async (print = false) => {
    setProcessing(true);
    try {
      const oid = await ensureOrder();
      await api.post(`/orders/${oid}/confirm`).catch(() => {});
      const { data } = await api.post(`/orders/${oid}/checkout`, { payments: payments.map((p) => ({ method: p.method, amount: Number(p.amount) })), tip: Number(tip || 0) });
      setPayOpen(false);
      setCart([]); setDiscount(0); setCustomer(null); setSelectedTable(null); setExistingOrderId(null); setOrderNotes(''); setTip(0); setDeliveryAddress(''); setKotSent(false); setExtOrderNo('');
      clearDraft();
      loadTables();
      if (print) { setInvoice(data.invoice); setTimeout(() => window.print(), 400); }
      toast.success(`Payment completed · Invoice ${data.invoice.invoice_no}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Checkout failed'); }
    finally { setProcessing(false); }
  };

  const paySum = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const payPackages = (config?.business?.discount_packages || []).filter((p) => p.active);

  // keep the single-payment amount in sync when a discount changes the total mid-settlement
  useEffect(() => {
    if (payOpen && payments.length === 1) {
      const amt = Number(total.toFixed(2));
      if (Number(payments[0].amount) !== amt) setPayments([{ method: payments[0].method, amount: amt }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total, payOpen]);

  const statusColor = { available: 'text-st-available', occupied: 'text-st-occupied', reserved: 'text-st-reserved', billed: 'text-st-billed', needs_cleaning: 'text-st-cleaning' };

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-3.5rem)]">
      {/* Catalog */}
      <div className="flex-1 flex flex-col min-w-0 p-3 md:p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="pos-search" className="pl-9 h-9" placeholder="Search name or code…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {menusList.length > 1 && (
            <select className="h-9 rounded-md border bg-background px-2 text-sm" data-testid="pos-pricelist-select"
              value={selectedMenuId || ''} title="Pricelist (menu set)"
              onChange={(e) => changePricelist(e.target.value)}>
              {menusList.map((m) => <option key={m.id} value={m.id}>{m.name}{m.is_active ? ' ★' : ''}</option>)}
            </select>
          )}
          <ToggleGroup type="single" value={orderType} onValueChange={(v) => v && setOrderType(v)}>
            {enabledOrderTypes.filter((t) => t !== 'qr').map((t) => (
              <ToggleGroupItem key={t} value={t} data-testid={`ot-${t.replace('-', '')}`} className="capitalize h-9">{t}</ToggleGroupItem>
            ))}
          </ToggleGroup>
          {orderType === 'dine-in' && (
            <Button variant={selectedTable ? 'default' : 'outline'} className="h-9" data-testid="pos-select-table" onClick={() => setTableOpen(true)}>
              <Grid3x3 className="h-4 w-4 mr-1" /> {selectedTable ? selectedTable.name : 'Select Table'}
            </Button>
          )}
          <Button variant="outline" className="h-9" data-testid="pos-new-order" onClick={newOrder}>
            <Plus className="h-4 w-4 mr-1" /> New Order
          </Button>
        </div>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2 mb-2">
          <button data-testid="cat-all" onClick={() => setActiveCat('all')} className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap border transition-colors ${activeCat === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-muted'}`}>All</button>
          {cats.map((c) => (
            <button key={c.id} data-testid={`pos-category-tile-${c.name}`} onClick={() => setActiveCat(c.id)} className={`px-3.5 py-1.5 rounded-full text-sm whitespace-nowrap border transition-colors ${activeCat === c.id ? 'bg-primary text-primary-foreground border-primary' : 'bg-card hover:bg-muted'}`}>{c.name}</button>
          ))}
        </div>
        <ScrollArea className="flex-1">
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm" data-testid="pos-search-empty">
              <Search className="h-10 w-10 mx-auto mb-2 opacity-30" />
              {search ? `No items match "${search}"` : 'No items in this category'}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 pb-4">
              {filtered.map((item) => (
                <div key={item.id} data-testid={`pos-menu-item-card-${item.name}`} onClick={() => (item.option_groups?.length ? openCYO(item) : (item.variants?.length ? null : addToCart(item)))}
                  className="rounded-xl border bg-card p-2 flex flex-col gap-1.5 cursor-pointer hover:shadow-sm hover:border-primary/40 active:scale-[0.98] transition-all">
                  {item.image && <img src={item.image} alt={item.name} className="w-full h-20 object-cover rounded-lg" loading="lazy" />}
                  <div className="flex items-start gap-1.5">
                    <span className={`h-2.5 w-2.5 rounded-full mt-1 shrink-0 ${item.veg ? 'bg-success' : 'bg-destructive'}`} />
                    <div className="font-medium text-sm leading-tight flex-1">{item.name}</div>
                  </div>
                  <div className="font-semibold text-sm">{fmtAED(priceFor(item))}</div>
                  {item.option_groups?.length ? (
                    <div className="text-[11px] text-primary font-medium" data-testid={`pos-customize-${item.name}`}>+ Build Your Own</div>
                  ) : item.variants?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {item.variants.map((v) => (
                        <Button key={v.name} size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={(e) => { e.stopPropagation(); addToCart(item, v); }}>{v.name} · {fmtAED(variantPriceFor(v))}</Button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      <Dialog open={!!cyoItem} onOpenChange={(o) => { if (!o) { setCyoItem(null); setCyoSel({}); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="cyo-dialog">
          <DialogHeader><DialogTitle>Build Your Own · {cyoItem?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {(cyoItem?.option_groups || []).map((g, gi) => {
              const sel = cyoSel[gi] || [];
              return (
                <div key={gi}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-sm font-semibold">{g.name}</div>
                    <Badge variant={sel.length >= (g.min || 1) ? 'secondary' : 'outline'} className="text-[10px]">{sel.length}/{g.max || 1}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {g.options.map((opt, oi) => (
                      <Button key={oi} size="sm" variant={sel.includes(oi) ? 'default' : 'outline'} className="h-8 text-xs"
                        data-testid={`cyo-opt-${gi}-${oi}`} onClick={() => toggleOpt(gi, oi, g.max || 1)}>
                        {opt.name}{opt.price_delta ? ` (+${fmtAED(opt.price_delta)})` : ''}
                      </Button>
                    ))}
                  </div>
                </div>
              );
            })}
            <Button className="w-full" data-testid="cyo-add-button" disabled={!cyoValid} onClick={addConfigured}>
              Add to Order · {fmtAED(cyoItem?.price || 0)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cart */}
      <div className="w-full lg:w-[380px] shrink-0 border-l bg-card flex flex-col" data-testid="pos-cart-panel">
        <div className="px-4 py-2 border-b flex items-center gap-2 text-sm">
          {orderType === 'dine-in' ? (
            selectedTable ? (
              <>
                <Grid3x3 className="h-4 w-4 text-primary" /><b>{selectedTable.name}</b>
                {existingOrderId && <Badge variant="outline" className="text-[10px]">running</Badge>}
                <button className="ml-auto text-muted-foreground hover:text-foreground" data-testid="pos-detach-table" onClick={detachTable} title="Detach table (keeps cart)"><X className="h-3.5 w-3.5" /></button>
              </>
            ) : (
              <>
                <span className="text-muted-foreground">Dine-In</span>
                <Button size="sm" variant="outline" className="h-7 ml-auto" data-testid="pos-cart-pick-table-button" onClick={() => setTableOpen(true)}>Pick Table</Button>
              </>
            )
          ) : (
            <span className="capitalize font-medium">{orderType}</span>
          )}
          {customer ? (
            <div className="flex items-center gap-1 text-xs ml-auto">
              <Badge variant="secondary" className="capitalize">{customer.name} · {customer.points}pts</Badge>
              <button className="text-muted-foreground hover:text-foreground" onClick={() => setCustomer(null)}><X className="h-3.5 w-3.5" /></button>
            </div>
          ) : (
            <button data-testid="pos-attach-customer" className="text-xs text-primary ml-auto" onClick={() => setCustOpen(true)}>+ Customer</button>
          )}
          {kotSent && <Badge className="bg-success text-white" data-testid="pos-kot-sent-badge">KOT Sent</Badge>}
        </div>
        {orderType === 'agent' && (
          <div className="px-4 py-2 border-b flex items-center gap-2">
            <select className="h-8 rounded-md border bg-background px-2 text-sm flex-1" data-testid="pos-agent-channel"
              value={orderChannel} onChange={(e) => setOrderChannel(e.target.value)}>
              {AGGREGATOR_CHANNELS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <Input className="h-8 w-32 text-sm" placeholder="App order #" data-testid="pos-external-order-no"
              value={extOrderNo} onChange={(e) => setExtOrderNo(e.target.value)} />
          </div>
        )}
        {orderType === 'delivery' && (
          <div className="px-4 py-2 border-b">
            <Textarea data-testid="pos-delivery-address" value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)}
              placeholder="Delivery address (building, street, area, city)…" className="text-sm min-h-[44px] h-11" />
            {customer?.address && !deliveryAddress && <button className="text-[11px] text-primary mt-1" onClick={() => setDeliveryAddress(customer.address)}>Use {customer.name}'s saved address</button>}
          </div>
        )}
        <ScrollArea className="flex-1 min-h-0 px-4">
          {cart.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground text-sm">
              <ShoppingCart className="h-10 w-10 mx-auto mb-2 opacity-30" />
              Tap items to add
            </div>
          ) : (
            <div className="py-3 space-y-2">
              {cart.map((c) => (
                <div key={c.key} className="space-y-1" data-testid={`cart-line-${c.name}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.name}</div>
                      {c.modifiers?.length > 0 && <div className="text-[11px] text-muted-foreground leading-tight">{c.modifiers.map((m) => m.name).join(', ')}</div>}
                      <div className="text-xs text-muted-foreground font-mono">{fmtAED(c.unit_price)}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant={c.notes ? 'secondary' : 'ghost'} className="h-7 w-7" title="Item note" data-testid={`cart-note-btn-${c.name}`} onClick={() => openKotNote(c)}><StickyNote className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => changeQty(c.key, -1)}><Minus className="h-3 w-3" /></Button>
                      <span className="w-6 text-center text-sm" data-testid={`cart-qty-${c.name}`}>{c.qty}</span>
                      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => changeQty(c.key, 1)}><Plus className="h-3 w-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeItem(c.key)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                  {c.notes && (
                    <div className="flex items-center gap-1 text-[11px] text-primary bg-primary/10 rounded-md px-2 py-1">
                      <StickyNote className="h-3 w-3 shrink-0" />
                      <span className="flex-1 truncate" data-testid={`cart-note-${c.name}`}>{c.notes}</span>
                      <button className="opacity-70 hover:opacity-100" data-testid={`cart-note-clear-${c.name}`} onClick={() => setLineNote(c.key, '')}><X className="h-3 w-3" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="p-3 border-t space-y-2 pb-3">
          <div className="flex items-center gap-2">
            <Button variant={orderNotes ? 'secondary' : 'outline'} className="h-8 text-xs px-2.5" data-testid="kot-note-button" onClick={openKotNoteOrder}>
              <StickyNote className="h-3.5 w-3.5 mr-1" /> KOT Note
            </Button>
            {orderNotes && <span className="flex-1 truncate text-[11px] text-primary" data-testid="kot-order-note-text" title={orderNotes}>{orderNotes}</span>}
            {orderNotes && <button className="text-muted-foreground hover:text-foreground shrink-0" data-testid="kot-order-note-clear" onClick={() => setOrderNotes('')} title="Clear note"><X className="h-3.5 w-3.5" /></button>}
          </div>
          <div className="flex items-center gap-1 flex-wrap" data-testid="pos-discount-presets">
            <span className="text-[11px] text-muted-foreground mr-0.5">Discount</span>
            {[5, 10, 15, 20].map((p) => (
              <Button key={p} size="sm" variant={discountType === 'percent' && Number(discount) === p ? 'default' : 'outline'} className="h-6 text-[11px] px-1.5"
                data-testid={`pos-discount-preset-${p}`} onClick={() => { setDiscountType('percent'); setDiscount(p); }}>{p}%</Button>
            ))}
            <Input type="number" className="h-6 w-16 text-[11px] px-1.5" placeholder="0" value={discount || ''} onChange={(e) => setDiscount(e.target.value)} data-testid="pos-discount-input" />
            <ToggleGroup type="single" value={discountType} onValueChange={(v) => v && setDiscountType(v)}>
              <ToggleGroupItem value="flat" className="h-6 text-[11px] px-1.5">AED</ToggleGroupItem>
              <ToggleGroupItem value="percent" className="h-6 text-[11px] px-1.5">%</ToggleGroupItem>
            </ToggleGroup>
            {(discount > 0) && <Button size="sm" variant="ghost" className="h-6 text-[11px] px-1.5" data-testid="pos-discount-clear" onClick={() => setDiscount(0)}>Clear</Button>}
          </div>
          <div className="text-sm space-y-0.5">
            <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span className="font-mono" data-testid="pos-subtotal">{fmtAED(subtotal)}</span></div>
            {discVal > 0 && <div className="flex justify-between text-destructive"><span>Discount</span><span className="font-mono">-{fmtAED(discVal)}</span></div>}
            <div className="flex justify-between text-muted-foreground"><span>VAT (5%)</span><span className="font-mono" data-testid="pos-vat">{fmtAED(vat)}</span></div>
            {serviceCharge > 0 && <div className="flex justify-between text-muted-foreground"><span>Service ({Math.round(svcRate * 100)}%)</span><span className="font-mono" data-testid="pos-service">{fmtAED(serviceCharge)}</span></div>}
            <div className="flex justify-between font-display font-bold text-lg pt-0.5"><span>Total</span><span className="font-mono" data-testid="pos-total">{fmtAED(total)}</span></div>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <Button data-testid="pos-save-button" variant="outline" className="h-9 text-sm" disabled={!cart.length || processing} onClick={saveOrder}>Save</Button>
            <Button data-testid="pos-hold-button" variant="outline" className="h-9 text-sm" disabled={!cart.length || processing} onClick={saveOrder}>Hold</Button>
            <Button data-testid="pos-print-bill-button" variant="outline" className="h-9 text-sm" disabled={!cart.length} onClick={printBill}><Printer className="h-3.5 w-3.5 mr-1" /> Bill</Button>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <Button data-testid="pos-send-kitchen-button" variant="outline" className="h-11" disabled={!cart.length || processing} onClick={sendToKitchen}><ChefHat className="h-4 w-4 mr-1" /> Send KOT</Button>
            <Button data-testid="pos-checkout-button" className="h-11" disabled={!cart.length} onClick={startCheckout}>Charge</Button>
          </div>
        </div>
      </div>

      {/* Table picker dialog */}
      <Dialog open={tableOpen} onOpenChange={setTableOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Select a Table</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Green = available · Amber = occupied (tap to continue its running order).{cart.length > 0 ? ' Your cart items are never lost when switching.' : ''}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-80 overflow-y-auto">
            {tables.map((t) => (
              <button key={t.id} data-testid={`pos-table-opt-${t.name}`} onClick={() => pickTable(t)}
                className="rounded-xl border-2 p-3 text-center hover:bg-muted transition-colors" style={{ borderColor: `hsl(var(--st-${t.status === 'needs_cleaning' ? 'cleaning' : t.status}))` }}>
                <div className="font-display font-bold">{t.name}</div>
                <div className={`text-[10px] capitalize ${statusColor[t.status] || ''}`}>{t.status.replace('_', ' ')}</div>
                <div className="text-[10px] text-muted-foreground">{t.seats} seats · {t.section}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Occupied-table conflict — never silently overwrite/merge an unsaved cart */}
      <Dialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
        <DialogContent className="max-w-sm" data-testid="table-conflict-dialog">
          <DialogHeader><DialogTitle>{conflict?.table.name} is occupied</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Your cart has {cart.length} unsaved item(s). Choose what to do — nothing is changed until you pick.</p>
          <div className="space-y-2 pt-1">
            <Button className="w-full" data-testid="conflict-keep-cart" onClick={conflictKeepCart}>Keep my cart — start a new check here</Button>
            {existingOrderId && (
              <Button variant="secondary" className="w-full" data-testid="conflict-transfer" onClick={conflictTransfer}>Transfer this order to {conflict?.table.name}</Button>
            )}
            <Button variant="outline" className="w-full" data-testid="conflict-open-check" onClick={conflictOpenCheck}>Discard my cart — open existing check</Button>
            <Button variant="ghost" className="w-full" data-testid="conflict-cancel" onClick={() => setConflict(null)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Check picker — a table with multiple checks lets you open each separately */}
      <Dialog open={checkPickOpen} onOpenChange={setCheckPickOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{selectedTable?.name} — {tableChecks.length} checks</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {tableChecks.map((c, i) => (
              <button key={c.id} data-testid={`pos-check-${i + 1}`} onClick={() => { loadCheck(c); setCheckPickOpen(false); }}
                className="w-full rounded-lg border p-3 flex items-center justify-between hover:border-primary hover:bg-muted transition-colors text-left">
                <div>
                  <div className="font-medium text-sm">Check {i + 1} <span className="text-muted-foreground font-normal">· {c.order_no}</span></div>
                  <div className="text-[11px] text-muted-foreground">{(c.items || []).length} item(s) · {c.status}</div>
                </div>
                <div className="font-mono text-sm">{fmtAED(c.grand_total)}</div>
              </button>
            ))}
            <Button variant="outline" className="w-full" data-testid="pos-check-new" onClick={() => { setCheckPickOpen(false); freshCheckState(); toast.info(`New check on ${selectedTable?.name}`); }}>
              <Plus className="h-4 w-4 mr-1" /> New Check
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer search dialog */}
      <Dialog open={custOpen} onOpenChange={setCustOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Attach Customer</DialogTitle></DialogHeader>
          <Input data-testid="cust-search-input" placeholder="Search name or phone…" value={custQuery} onChange={(e) => searchCust(e.target.value)} />
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {custResults.map((c) => (
              <button key={c.id} data-testid={`cust-result-${c.phone}`} className="w-full text-left p-2 rounded-lg hover:bg-muted flex justify-between" onClick={() => { setCustomer(c); setCustOpen(false); setCustQuery(''); setCustResults([]); }}>
                <span>{c.name}</span><span className="text-muted-foreground text-sm">{c.phone}</span>
              </button>
            ))}
            {custQuery.length >= 2 && custResults.length === 0 && <div className="text-sm text-muted-foreground p-2">No match. Add customers from the Customers page.</div>}
          </div>
        </DialogContent>
      </Dialog>

      {/* KOT Note dialog (order-level or per-item) */}
      <Dialog open={!!noteTarget} onOpenChange={(o) => !o && setNoteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="kot-note-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><StickyNote className="h-4 w-4" /> KOT Note — {noteTarget === 'order' ? 'This Order' : cart.find((c) => c.key === noteTarget)?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Quick instructions</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {KOT_PRESETS.map((p) => (
                  <button key={p} data-testid={`kot-preset-${p}`} onClick={() => addPreset(p)}
                    className="rounded-full border px-2.5 py-1 text-xs hover:bg-muted transition-colors">{p}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Or type a custom note</Label>
              <Textarea data-testid="kot-note-text" value={noteText} onChange={(e) => setNoteText(e.target.value)}
                placeholder="e.g. no onions, extra spicy, pack hot items separately…" className="text-sm min-h-[64px] mt-1" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" data-testid="kot-note-clear" onClick={() => { setNoteText(''); }}>Clear</Button>
              <Button className="flex-1" data-testid="kot-note-attach" onClick={attachKotNote}>Attach Note</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Payment — {fmtAED(total)}</DialogTitle></DialogHeader>
          <div className="space-y-1.5 border rounded-lg p-2.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground">DISCOUNT</span>
              {Number(discount) > 0 && <button className="text-[11px] text-destructive" data-testid="pay-discount-clear" onClick={() => setDiscount(0)}>Remove</button>}
            </div>
            {payPackages.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {payPackages.map((p) => (
                  <button key={p.id} data-testid={`pay-discount-pkg-${p.name}`}
                    onClick={() => { setDiscount(p.value); setDiscountType(p.type); }}
                    className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${Number(discount) === p.value && discountType === p.type ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
                    {p.name} · {p.type === 'percent' ? `${p.value}%` : `AED ${p.value}`}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 items-center">
              <Input type="number" className="h-8 w-28" placeholder="Custom" value={discount || ''} onChange={(e) => setDiscount(e.target.value)} data-testid="pay-discount-input" />
              <ToggleGroup type="single" value={discountType} onValueChange={(v) => v && setDiscountType(v)}>
                <ToggleGroupItem value="flat" className="h-8 px-2">AED</ToggleGroupItem>
                <ToggleGroupItem value="percent" className="h-8 px-2">%</ToggleGroupItem>
              </ToggleGroup>
              {discVal > 0 && <span className="text-xs text-destructive ml-auto font-mono">−{fmtAED(discVal)}</span>}
            </div>
          </div>
          <Tabs defaultValue="single">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="single" onClick={() => setPayments([{ method: 'cash', amount: Number(total.toFixed(2)) }])}>Single</TabsTrigger>
              <TabsTrigger value="split" data-testid="pos-split-tab" onClick={() => setPayments([{ method: 'cash', amount: 0 }, { method: 'card', amount: 0 }])}>Split</TabsTrigger>
            </TabsList>
            <TabsContent value="single" className="space-y-3 pt-3">
              <ToggleGroup type="single" value={payments[0]?.method} onValueChange={(v) => v && setPayments([{ method: v, amount: Number(total.toFixed(2)) }])} className="flex-wrap">
                {payMethods.map((m) => <ToggleGroupItem key={m} value={m} data-testid={`pay-method-${m}`} className="capitalize">{m.replace('_', ' ')}</ToggleGroupItem>)}
              </ToggleGroup>
            </TabsContent>
            <TabsContent value="split" className="space-y-2 pt-3">
              {payments.map((p, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <select className="h-9 rounded-md border bg-background px-2 text-sm capitalize" value={p.method} onChange={(e) => setPayments(payments.map((x, j) => j === i ? { ...x, method: e.target.value } : x))}>
                    {payMethods.map((m) => <option key={m} value={m}>{m.replace('_', ' ')}</option>)}
                  </select>
                  <Input type="number" value={p.amount || ''} data-testid={`split-amount-${i}`} onChange={(e) => setPayments(payments.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} />
                </div>
              ))}
              <Button size="sm" variant="outline" data-testid="pos-split-payment-add-row-button" onClick={() => setPayments([...payments, { method: payMethods[0], amount: 0 }])}>+ Add split</Button>
              <div className="text-sm flex justify-between pt-1"><span>Paid</span><span className={paySum >= total - 0.01 ? 'text-success' : 'text-destructive'}>{fmtAED(paySum)} / {fmtAED(total)}</span></div>
            </TabsContent>
          </Tabs>
          {tipEnabled && (
            <div className="flex items-center gap-2 pt-1">
              <Label className="text-xs whitespace-nowrap">Tip (AED)</Label>
              <Input type="number" className="h-9" data-testid="pos-tip-input" value={tip || ''} onChange={(e) => setTip(e.target.value)} placeholder="0.00" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="pos-payment-confirm-button" className="h-11" disabled={processing || paySum < total - 0.01} onClick={() => finalize(false)}>
              {processing ? 'Processing…' : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Complete</>}
            </Button>
            <Button data-testid="pos-payment-confirm-print-button" variant="outline" className="h-11" disabled={processing || paySum < total - 0.01} onClick={() => finalize(true)}>
              {processing ? 'Processing…' : <><Printer className="h-4 w-4 mr-1.5" /> Complete & Print</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice dialog */}
      <Dialog open={!!invoice} onOpenChange={(o) => !o && setInvoice(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Tax Invoice</DialogTitle></DialogHeader>
          {invoice && <InvoiceView invoice={invoice} footer={config?.receipt_footer} />}
          <div className="flex gap-2">
            <Button data-testid="invoice-print-button" className="flex-1" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" /> Print</Button>
            <Button variant="outline" className="flex-1" onClick={() => setInvoice(null)}>New Order</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
